import { findAfter, findBefore } from "./data-lookup.js";
export const searchAlternatives = (milli, timingPrecision, index, alternatives) => {
    let value = null;
    if (alternatives && alternatives.length > 0) {
        let i = 0;
        while (value == null && i < alternatives.length) {
            value = alternatives[i].get(milli, timingPrecision);
        }
    }
    return value != null ? { t: milli, v: value, i: index } : undefined;
};
const checkE1 = (neighborhood, gapDetectionThreshold, closeProximity, alternatives) => {
    //now check if e1 exists or is not too far from e0.
    if (neighborhood.e0) {
        if (!neighborhood.e1 ||
            neighborhood.e1.t - neighborhood.e0.t > gapDetectionThreshold) {
            const e1t = neighborhood.e0.t + closeProximity;
            neighborhood.e1 = searchAlternatives(e1t, closeProximity, neighborhood.e0.i, alternatives);
        }
    }
};
const checkS1 = (neighborhood, gapDetectionThreshold, closeProximity, alternatives) => {
    //now check if s1 exists or is not too far from s0.
    if (neighborhood.s0) {
        if (!neighborhood.s1 ||
            neighborhood.s0.t - neighborhood.s1.t > gapDetectionThreshold) {
            const s1t = neighborhood.s0.t - closeProximity;
            neighborhood.s1 = searchAlternatives(s1t, closeProximity, neighborhood.s0.i, alternatives);
        }
    }
};
const constructS0S1 = (neighborhood, closeProximity, alternatives) => {
    //construct s0 and s1 from alternatives
    if (neighborhood.e0) {
        const s0t = neighborhood.e0.t - closeProximity;
        const s1t = s0t - closeProximity;
        neighborhood.s0 = searchAlternatives(s0t, closeProximity, neighborhood.e0.i - 1, alternatives);
        neighborhood.s1 = searchAlternatives(s1t, closeProximity, neighborhood.e0.i - 1, alternatives);
    }
};
const constructE0E1 = (neighborhood, closeProximity, alternatives) => {
    //construct e0 and e1 from alternatives
    if (neighborhood.s0) {
        const e0t = neighborhood.s0.t + closeProximity;
        const e1t = e0t + closeProximity;
        neighborhood.e0 = searchAlternatives(e0t, closeProximity, neighborhood.s0.i + 1, alternatives);
        neighborhood.e1 = searchAlternatives(e1t, closeProximity, neighborhood.s0.i + 1, alternatives);
    }
};
const buildFromE = (milli, from, neighborhood, gapDetectionThreshold, closeProximity, alternatives) => {
    if (neighborhood.e0) {
        const deltaE0 = neighborhood.e0.t - milli;
        if (deltaE0 > closeProximity) {
            if (deltaE0 > 2 * closeProximity) {
                //e0 lies to far
                return buildNeighborhood(milli, closeProximity, from, alternatives);
            }
            else {
                //e0 can play the role of e1
                const e0t = neighborhood.e0.t - closeProximity;
                const e0 = searchAlternatives(e0t, closeProximity, neighborhood.e0.i - 1, alternatives);
                if (e0) {
                    //we found an alternative for e0: e0 now becomes  e1 and the alternative becomes e0
                    neighborhood.e1 = neighborhood.e0;
                    neighborhood.e0 = e0;
                }
                else {
                    //did not find an alternative for e0 so we are left empty handed
                    return undefined;
                }
            }
        }
        //at this point we have proper value for e0
        //now check if e1 exists or is not too far from e0.
        checkE1(neighborhood, gapDetectionThreshold, closeProximity, alternatives);
        //construct s0 and s1 from alternatives
        constructS0S1(neighborhood, closeProximity, alternatives);
    }
    //we are done here
    return neighborhood;
};
const buildFromS = (milli, from, neighborhood, gapDetectionThreshold, closeProximity, alternatives) => {
    if (neighborhood.s0) {
        const deltaS0 = milli - neighborhood.s0.t;
        if (deltaS0 > closeProximity) {
            if (deltaS0 > 2 * closeProximity) {
                //s0 lies to far
                return buildNeighborhood(milli, closeProximity, from, alternatives);
            }
            else {
                //s0 can play the role of s1
                const s0t = neighborhood.s0.t + closeProximity;
                const s0 = searchAlternatives(s0t, closeProximity, neighborhood.s0.i + 1, alternatives);
                if (s0 != null) {
                    //we found an alternative for s0: s0 now becomes  s1 and the alternative becomes s0
                    neighborhood.s1 = neighborhood.s0;
                    neighborhood.s0 = s0;
                }
                else {
                    //did not find an alternative for s0 so we are left empty handed
                    return undefined;
                }
            }
        }
        //at this point we have proper value for s0
        //now check if s1 exists or is not too far from s0.
        checkS1(neighborhood, gapDetectionThreshold, closeProximity, alternatives);
        //construct e0 and e1 from alternatives
        constructE0E1(neighborhood, closeProximity, alternatives);
    }
    //we are done here
    return neighborhood;
};
const buildNeighborhood = (milli, closeProximity, from, alternatives) => {
    const offset = milli % closeProximity;
    const e0t = milli - (offset > 0 ? offset - closeProximity : 0);
    //find first defined value with a timestamp >= t
    const e0 = searchAlternatives(e0t, closeProximity, from, alternatives);
    if (e0 != null) {
        //found the first defined value with a timestamp >= t
        const s0t = e0t - closeProximity;
        const s1t = s0t - closeProximity;
        const e1t = e0t + closeProximity;
        const s0 = searchAlternatives(s0t, closeProximity, from, alternatives);
        const e1 = searchAlternatives(e1t, closeProximity, from, alternatives);
        const s1 = s0
            ? searchAlternatives(s1t, closeProximity, from, alternatives)
            : undefined;
        return { s1, s0, e0, e1 };
    }
    return undefined;
};
export const getValueNeighborhood = (milli, time, data, gapDetectionThreshold, closeProximity, from, alternatives) => {
    const neighborhood = lookupValueNeighborhood(milli, time, data, from);
    if (neighborhood && neighborhood.e0 && neighborhood.e0.t === milli) {
        return neighborhood; //exact match. perfect
    }
    else {
        if (!neighborhood) {
            //no neighborhood
            return buildNeighborhood(milli, closeProximity, from, alternatives);
        }
        else {
            //we got a neighborhood. Lets validate it
            if (neighborhood.e0) {
                const deltaE0 = neighborhood.e0.t - milli;
                const deltaS0 = neighborhood.s0
                    ? milli - neighborhood.s0.t
                    : Number.POSITIVE_INFINITY;
                if (deltaS0 + deltaE0 > gapDetectionThreshold) {
                    //the data points surrounding t lie to far apart.
                    //This means that the signal has a gap and we should employ the alternatives
                    if (deltaE0 < deltaS0 && deltaE0 < 2 * closeProximity) {
                        return buildFromE(milli, from, neighborhood, gapDetectionThreshold, closeProximity, alternatives);
                    }
                    else if (deltaS0 < deltaE0 && deltaS0 < 2 * closeProximity) {
                        return buildFromS(milli, from, neighborhood, gapDetectionThreshold, closeProximity, alternatives);
                    }
                    else {
                        //to far from both s0 and e0
                        return buildNeighborhood(milli, closeProximity, from, alternatives);
                    }
                }
                else {
                    //both s0 and e0 are fine
                    checkS1(neighborhood, gapDetectionThreshold, closeProximity, alternatives);
                    checkE1(neighborhood, gapDetectionThreshold, closeProximity, alternatives);
                    return neighborhood;
                }
            }
            else {
                //neighborhood turns out to be empty
                return buildNeighborhood(milli, closeProximity, from, alternatives);
            }
        }
    }
};
const lookupValueNeighborhood = (milli, time, data, from = 0) => {
    //find first defined value with a timestamp >= t
    let e0 = findAfter(milli, time, data, from);
    if (e0 !== undefined) {
        //found the first defined value with a timestamp >= t
        let s0 = undefined;
        let s1 = undefined;
        let e1 = undefined;
        if (time(e0) !== milli) {
            //we need a spline here so retrieve the local neighborhood
            s0 = findBefore(time(e0), time, data, e0 - 1);
            e1 = findAfter(time(e0), time, data, e0 + 1);
            s1 = s0 !== undefined ? findBefore(time(s0), time, data, s0 - 1) : s1;
        }
        return {
            s1: s1 !== undefined
                ? { t: time(s1), v: data(s1), i: s1 }
                : undefined,
            s0: s0 !== undefined
                ? { t: time(s0), v: data(s0), i: s0 }
                : undefined,
            e0: { t: time(e0), v: data(e0), i: e0 },
            e1: e1 !== undefined
                ? { t: time(e1), v: data(e1), i: e1 }
                : undefined,
        };
    }
    return undefined;
};
